/**
 * AddExistingProjectDialog
 *
 * Reusable dialog for adding an existing unified project to an app.
 * Shows projects that don't have the given app enabled.
 */

import React, { useState, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    TextField,
    InputAdornment,
    Chip,
    CircularProgress,
    alpha,
} from '@mui/material';
import {
    Search as SearchIcon,
    LocationOn as LocationIcon,
    Folder as FolderIcon,
} from '@mui/icons-material';
import { useUnifiedProjects } from '../hooks/useUnifiedProjects';
import { vortxColors, retroColors, vortxTypography } from '../core/theme';

const APP_LABELS = {
    site_dna: 'Site DNA',
    flux_ev: 'Flux EV',
    flux_ems: 'Flux EMS',
    flux_cold: 'Flux COLD',
    flux_phd: 'Flux PhD',
};

const APP_COLORS = {
    site_dna: vortxColors.gold,
    flux_ev: '#4CAF50',
    flux_ems: '#5fd4c4',
    flux_cold: '#2196F3',
    flux_phd: '#9b59b6',
};

export default function AddExistingProjectDialog({
    open,
    onClose,
    appName,
    appLabel,
    onProjectAdded,
}) {
    const { projects, loading, enableApp } = useUnifiedProjects(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    const [enabling, setEnabling] = useState(false);

    // Filter to projects that don't have this app enabled
    const eligibleProjects = useMemo(() => {
        return projects.filter(p => !p.apps?.[appName]?.enabled);
    }, [projects, appName]);

    // Further filter by search query
    const filteredProjects = useMemo(() => {
        if (!searchQuery) return eligibleProjects;
        const q = searchQuery.toLowerCase();
        return eligibleProjects.filter(p =>
            p.name.toLowerCase().includes(q) ||
            (p.location?.address || '').toLowerCase().includes(q)
        );
    }, [eligibleProjects, searchQuery]);

    const handleSelect = async (project) => {
        setSelectedProjectId(project.id);
        setEnabling(true);
        try {
            const updatedProject = await enableApp(project.id, appName, {});
            onProjectAdded?.(updatedProject);
            handleClose();
        } catch (err) {
            console.error('Failed to enable app for project:', err);
        } finally {
            setEnabling(false);
            setSelectedProjectId(null);
        }
    };

    const handleClose = () => {
        setSearchQuery('');
        setSelectedProjectId(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={!enabling ? handleClose : undefined}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    background: `linear-gradient(135deg, ${retroColors.bg} 0%, ${retroColors.surface} 100%)`,
                    border: `1px solid ${alpha(vortxColors.gold, 0.3)}`,
                    boxShadow: `0 0 30px ${alpha(vortxColors.gold, 0.2)}`,
                }
            }}
        >
            <DialogTitle sx={{
                fontFamily: vortxTypography.heading,
                fontWeight: 700,
                color: vortxColors.gold,
                borderBottom: `1px solid ${alpha(vortxColors.gold, 0.2)}`,
                pb: 2,
            }}>
                Add Existing Project to {appLabel || APP_LABELS[appName] || appName}
            </DialogTitle>
            <DialogContent sx={{ pt: 3, minHeight: 300 }}>
                {/* Search */}
                <TextField
                    placeholder="Search projects..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size="small"
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: alpha(vortxColors.gold, 0.5), fontSize: 18 }} />
                            </InputAdornment>
                        )
                    }}
                    sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            color: vortxColors.white,
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.8rem',
                            '& fieldset': { borderColor: alpha(vortxColors.gold, 0.3) },
                            '&:hover fieldset': { borderColor: alpha(vortxColors.gold, 0.5) },
                            '&.Mui-focused fieldset': { borderColor: vortxColors.gold },
                        },
                        '& .MuiOutlinedInput-input::placeholder': {
                            color: alpha(vortxColors.stone, 0.5),
                            opacity: 1,
                        },
                    }}
                />

                {/* Loading */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={28} sx={{ color: vortxColors.gold }} />
                    </Box>
                )}

                {/* Empty State */}
                {!loading && filteredProjects.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.8rem',
                            color: retroColors.muted,
                        }}>
                            {eligibleProjects.length === 0
                                ? `All projects already have ${appLabel || APP_LABELS[appName]} enabled`
                                : 'No projects match your search'}
                        </Typography>
                    </Box>
                )}

                {/* Project List */}
                {!loading && filteredProjects.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {filteredProjects.map((project) => {
                            const enabledApps = Object.entries(project.apps || {})
                                .filter(([_, c]) => c.enabled)
                                .map(([app]) => app);
                            const isSelecting = selectedProjectId === project.id;

                            return (
                                <Box
                                    key={project.id}
                                    onClick={() => !enabling && handleSelect(project)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        p: 1.5,
                                        cursor: enabling ? 'wait' : 'pointer',
                                        border: `1px solid ${alpha(vortxColors.gold, 0.15)}`,
                                        background: isSelecting
                                            ? alpha(vortxColors.gold, 0.1)
                                            : 'rgba(0, 0, 0, 0.3)',
                                        transition: 'all 0.15s ease',
                                        '&:hover': enabling ? {} : {
                                            borderColor: alpha(vortxColors.gold, 0.4),
                                            background: alpha(vortxColors.gold, 0.08),
                                        },
                                    }}
                                >
                                    {isSelecting ? (
                                        <CircularProgress size={18} sx={{ color: vortxColors.gold }} />
                                    ) : (
                                        <FolderIcon sx={{ fontSize: 18, color: alpha(vortxColors.gold, 0.6) }} />
                                    )}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{
                                            fontFamily: vortxTypography.mono,
                                            fontSize: '0.8rem',
                                            fontWeight: 600,
                                            color: vortxColors.white,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {project.name}
                                        </Typography>
                                        {project.location?.address && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                                <LocationIcon sx={{ fontSize: 10, color: retroColors.dimmed }} />
                                                <Typography sx={{
                                                    fontFamily: vortxTypography.mono,
                                                    fontSize: '0.6rem',
                                                    color: retroColors.dimmed,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {project.location.address}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                        {enabledApps.map(app => (
                                            <Chip
                                                key={app}
                                                label={APP_LABELS[app] || app}
                                                size="small"
                                                sx={{
                                                    height: 18,
                                                    fontSize: '0.55rem',
                                                    fontFamily: vortxTypography.mono,
                                                    fontWeight: 600,
                                                    backgroundColor: alpha(APP_COLORS[app] || vortxColors.teal, 0.15),
                                                    color: APP_COLORS[app] || vortxColors.white,
                                                    border: `1px solid ${alpha(APP_COLORS[app] || vortxColors.teal, 0.3)}`,
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{
                borderTop: `1px solid ${alpha(vortxColors.gold, 0.2)}`,
                px: 3,
                py: 2,
            }}>
                <Button
                    onClick={handleClose}
                    disabled={enabling}
                    sx={{
                        color: vortxColors.stone,
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        '&:hover': { backgroundColor: alpha(vortxColors.stone, 0.1) },
                    }}
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
